import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Briefcase, MapPin, Clock, DollarSign, Building2, Search,
  Filter, ChevronRight, ChevronLeft, Star, Zap, Users, ExternalLink,
  Calendar, CheckCircle, Tag, Award, Gift, Loader2, X, ArrowLeft,
  MessageSquare, Sparkles, FileText, Upload, SlidersHorizontal,
  Bell, BellOff, BookmarkPlus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 10;

const JobBoardPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [jobType, setJobType] = useState('');
  const [location, setLocation] = useState('');
  const [workMode, setWorkMode] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [postedWithin, setPostedWithin] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  // Resume Analysis
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [analysisJob, setAnalysisJob] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [uploadingResume, setUploadingResume] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  // Saved Searches
  const [savedSearches, setSavedSearches] = useState([]);
  const [savingSearch, setSavingSearch] = useState(false);
  const [appliedSavedId, setAppliedSavedId] = useState(null);
  
  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (token) fetchSavedSearches();
  }, [token]);

  const fetchSavedSearches = async () => {
    try {
      const res = await axios.get(`${API}/codearena/saved-searches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedSearches(res.data.saved_searches || []);
    } catch (err) {
      // Silent fail; saved-search panel just won't render
    }
  };

  const currentFilters = () => ({
    search: searchQuery,
    jobType,
    location,
    workMode,
    experienceLevel,
    postedWithin,
    sortBy,
  });

  const handleSaveSearch = async () => {
    if (!token) {
      toast.error('Please login to save searches');
      navigate('/codearena/auth?redirect=/codearena/job-board');
      return;
    }
    setSavingSearch(true);
    try {
      const res = await axios.post(`${API}/codearena/saved-searches`,
        { filters: currentFilters(), alerts_enabled: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedSearches(prev => [res.data, ...prev]);
      toast.success('Search saved! You\u2019ll get a daily email digest at 9 AM IST.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save search');
    } finally {
      setSavingSearch(false);
    }
  };

  const applySavedSearch = (s) => {
    const f = s.filters || {};
    setSearchQuery(f.search || '');
    setJobType(f.jobType || '');
    setLocation(f.location || '');
    setWorkMode(f.workMode || '');
    setExperienceLevel(f.experienceLevel || '');
    setPostedWithin(f.postedWithin || '');
    setSortBy(f.sortBy || 'newest');
    setAppliedSavedId(s.id);
    setShowFilters(true);
    toast.success(`Applied saved search: ${s.name}`);
  };

  const toggleSavedAlerts = async (s) => {
    try {
      const res = await axios.patch(`${API}/codearena/saved-searches/${s.id}`,
        { alerts_enabled: !s.alerts_enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedSearches(prev => prev.map(x => x.id === s.id ? { ...x, alerts_enabled: res.data.alerts_enabled } : x));
      toast.success(res.data.alerts_enabled ? 'Email alerts enabled' : 'Email alerts paused');
    } catch (err) {
      toast.error('Failed to update alerts');
    }
  };

  const deleteSavedSearch = async (s) => {
    if (!window.confirm(`Delete saved search "${s.name}"?`)) return;
    try {
      await axios.delete(`${API}/codearena/saved-searches/${s.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedSearches(prev => prev.filter(x => x.id !== s.id));
      if (appliedSavedId === s.id) setAppliedSavedId(null);
      toast.success('Saved search deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      // Fetch up to 1000 opportunities so client-side pagination shows ALL jobs
      const res = await axios.get(`${API}/codearena/job-board?skip=0&limit=1000`);
      setJobs(res.data.jobs || []);
      setCompetitions(res.data.competitions || []);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyForJob = async (jobId) => {
    if (!token) {
      toast.error('Please login to apply');
      return;
    }
    
    setApplying(true);
    try {
      await axios.post(`${API}/codearena/job-board/${jobId}/apply`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted successfully!');
      setSelectedJob(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  // ─── Strong filtering + sorting ─────────────────────────────────
  const uniqueLocations = useMemo(() => {
    const set = new Set();
    jobs.forEach(j => { if (j.location) set.add(j.location.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [jobs]);

  const parseSalary = (salaryRange) => {
    if (!salaryRange || typeof salaryRange !== 'string') return 0;
    // Extract first numeric value with optional L/Lac/LPA/k modifiers
    const m = salaryRange.match(/(\d+(?:\.\d+)?)\s*(L|LPA|Lac|Lakh|k|K)?/i);
    if (!m) return 0;
    const v = parseFloat(m[1]);
    const unit = (m[2] || '').toLowerCase();
    if (unit.startsWith('l')) return v * 100000;
    if (unit === 'k') return v * 1000;
    return v;
  };

  const matchesPostedWithin = (job, key) => {
    if (!key) return true;
    const created = job.created_at || job.posted_at;
    if (!created) return true; // don't exclude if missing
    const ts = new Date(created).getTime();
    if (Number.isNaN(ts)) return true;
    const now = Date.now();
    const days = key === '24h' ? 1 : key === '7d' ? 7 : key === '30d' ? 30 : null;
    if (!days) return true;
    return (now - ts) <= days * 24 * 60 * 60 * 1000;
  };

  const matchesWorkMode = (job, mode) => {
    if (!mode) return true;
    const explicit = (job.work_mode || '').toLowerCase();
    const loc = (job.location || '').toLowerCase();
    if (mode === 'remote') return explicit === 'remote' || loc.includes('remote');
    if (mode === 'hybrid') return explicit === 'hybrid' || loc.includes('hybrid');
    if (mode === 'onsite') return explicit === 'onsite' || explicit === 'on-site' || (!explicit && !loc.includes('remote') && !loc.includes('hybrid'));
    return true;
  };

  const processedJobs = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const locQ = location.trim().toLowerCase();
    let arr = jobs.filter(job => {
      // Search across title, company, skills, description, location
      if (q) {
        const hay = [
          job.title, job.company_name, job.location, job.description,
          ...(job.required_skills || []), ...(job.skills_required || [])
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Job type — normalize hyphen/underscore so 'full-time' matches 'full_time'
      if (jobType) {
        const want = jobType.toLowerCase().replace(/-/g, '_');
        const got = (job.job_type || '').toLowerCase().replace(/-/g, '_');
        if (got !== want) return false;
      }
      // Location substring match — flexible across 'Bangalore', 'Bangalore, India', 'Remote' etc.
      if (locQ) {
        const got = (job.location || '').toLowerCase();
        if (!got.includes(locQ)) return false;
      }
      // Experience — flexible match across DB schema (fresher, 0_1_years, 1_3_years, 3_5_years, 5_plus_years) and human labels
      if (experienceLevel) {
        const lvl = (job.experience_level || '').toLowerCase();
        const want = experienceLevel;
        const matchers = {
          fresher: ['fresher', '0_1_years', '0-1', 'entry'],
          junior:  ['junior', '1_3_years', '1-3'],
          mid:     ['mid', 'mid-level', '3_5_years', '3-5'],
          senior:  ['senior', '5_plus_years', '5+'],
        };
        const list = matchers[want] || [want];
        if (!list.some(m => lvl.includes(m))) return false;
      }
      if (!matchesWorkMode(job, workMode)) return false;
      if (!matchesPostedWithin(job, postedWithin)) return false;
      return true;
    });

    // Sort
    arr = [...arr].sort((a, b) => {
      if (sortBy === 'salary') return parseSalary(b.salary_range) - parseSalary(a.salary_range);
      if (sortBy === 'deadline') {
        const ad = a.application_deadline ? new Date(a.application_deadline).getTime() : Infinity;
        const bd = b.application_deadline ? new Date(b.application_deadline).getTime() : Infinity;
        return ad - bd;
      }
      // newest (default)
      const at = a.created_at ? new Date(a.created_at).getTime() : 0;
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
      return bt - at;
    });
    return arr;
  }, [jobs, searchQuery, jobType, location, experienceLevel, workMode, postedWithin, sortBy]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(processedJobs.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedJobs = useMemo(
    () => processedJobs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [processedJobs, safePage]
  );

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, jobType, location, workMode, experienceLevel, postedWithin, sortBy]);

  const activeFilters = [
    jobType && { key: 'jobType', label: `Type: ${jobType}`, clear: () => setJobType('') },
    location && { key: 'location', label: `Location: ${location}`, clear: () => setLocation('') },
    workMode && { key: 'workMode', label: `Work: ${workMode}`, clear: () => setWorkMode('') },
    experienceLevel && { key: 'exp', label: `Level: ${experienceLevel}`, clear: () => setExperienceLevel('') },
    postedWithin && { key: 'posted', label: `Posted: ${postedWithin}`, clear: () => setPostedWithin('') },
    searchQuery && { key: 'q', label: `“${searchQuery}”`, clear: () => setSearchQuery('') },
  ].filter(Boolean);

  const clearAllFilters = () => {
    setSearchQuery(''); setJobType(''); setLocation(''); setWorkMode('');
    setExperienceLevel(''); setPostedWithin(''); setSortBy('newest');
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files supported'); return; }
    setUploadingResume(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/mock-interview/upload-resume`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setResumeText(res.data.resume_text || '');
      toast.success('Resume text extracted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process resume');
    } finally { setUploadingResume(false); }
  };

  const runAnalysis = async () => {
    if (!resumeText.trim() || !analysisJob) return;
    setAnalyzing(true);
    try {
      const jdText = `${analysisJob.title} at ${analysisJob.company_name}\n\n${analysisJob.description || ''}${analysisJob.required_skills?.length ? `\n\nRequired Skills: ${analysisJob.required_skills.join(', ')}` : ''}`;
      const res = await axios.post(`${API}/mock-interview/resume-analysis`, {
        resume_text: resumeText,
        job_description: jdText
      }, { headers: { Authorization: `Bearer ${token}` }, timeout: 45000 });
      setAnalysisResult(res.data.analysis);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally { setAnalyzing(false); }
  };

  const getJobTypeStyle = (type) => {
    const styles = {
      internship: 'bg-[#0a66c2]/10 text-[#0a66c2] border-[#0a66c2]/30',
      'full-time': 'bg-[#057642]/10 text-[#057642] border-[#057642]/30',
      'part-time': 'bg-[#915907]/10 text-[#915907] border-[#915907]/30',
      contract: 'bg-[#5f4bb6]/10 text-[#5f4bb6] border-[#5f4bb6]/30'
    };
    return styles[type] || styles.internship;
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-3">
            <Link 
              to="/codearena" 
              className="flex items-center gap-2 text-[#666666] hover:text-[#191919] transition-colors text-sm"
            >
              <ArrowLeft size={16} />
              Back to EvalentX Arena
            </Link>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-[#191919] flex items-center gap-2">
                <Briefcase className="text-[#0a66c2]" size={28} />
                Job Board
              </h1>
              <p className="text-[#666666] text-sm mt-0.5">
                Discover opportunities from top companies. Apply directly and track your applications.
              </p>
            </div>
          </div>
          
          {/* Search & Filters */}
          <div className="mt-4 space-y-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666666]" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title, company, skill, or keyword…"
                  className="w-full pl-11 pr-10 py-2.5 bg-[#f3f2ef] border border-gray-300 rounded-lg text-[#191919] placeholder-[#666666] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] transition-colors"
                  data-testid="job-board-search"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#666666] hover:text-[#191919] p-0.5"
                    aria-label="Clear search"
                    data-testid="clear-search-btn"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] text-sm"
                data-testid="job-type-filter"
              >
                <option value="">All Types</option>
                <option value="internship">Internship</option>
                <option value="full-time">Full-time</option>
                <option value="part-time">Part-time</option>
                <option value="contract">Contract</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-[#191919] focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] text-sm"
                data-testid="job-sort"
              >
                <option value="newest">Sort: Newest</option>
                <option value="deadline">Sort: Closing soon</option>
                <option value="salary">Sort: Salary high to low</option>
              </select>
              <button
                onClick={() => setShowFilters(v => !v)}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 border rounded-lg text-sm font-medium transition-colors ${showFilters ? 'bg-[#0a66c2] text-white border-[#0a66c2]' : 'bg-white text-[#191919] border-gray-300 hover:border-[#0a66c2]'}`}
                data-testid="toggle-more-filters"
              >
                <SlidersHorizontal size={16} />
                More filters{activeFilters.length > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[11px] font-bold ${showFilters ? 'bg-white text-[#0a66c2]' : 'bg-[#0a66c2] text-white'}`}>{activeFilters.length}</span>
                )}
              </button>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-[#f3f2ef] border border-gray-200 rounded-lg" data-testid="more-filters-panel">
                <div>
                  <label className="block text-[11px] font-bold text-[#666666] uppercase tracking-wider mb-1.5">Location</label>
                  <input
                    list="job-board-locations"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Any location"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-[#191919] text-sm focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2]"
                    data-testid="location-filter"
                  />
                  <datalist id="job-board-locations">
                    <option value="Remote" />
                    <option value="Hybrid" />
                    {uniqueLocations.map(loc => <option key={loc} value={loc} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-[#666666] uppercase tracking-wider mb-1.5">Work mode</label>
                  <select
                    value={workMode}
                    onChange={(e) => setWorkMode(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-[#191919] text-sm focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2]"
                    data-testid="work-mode-filter"
                  >
                    <option value="">Any</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-site</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-[#666666] uppercase tracking-wider mb-1.5">Experience</label>
                  <select
                    value={experienceLevel}
                    onChange={(e) => setExperienceLevel(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-[#191919] text-sm focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2]"
                    data-testid="experience-filter"
                  >
                    <option value="">Any</option>
                    <option value="fresher">Fresher / Entry (0–1 yr)</option>
                    <option value="junior">Junior (1–3 yrs)</option>
                    <option value="mid">Mid-level (3–5 yrs)</option>
                    <option value="senior">Senior (5+ yrs)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-[#666666] uppercase tracking-wider mb-1.5">Posted within</label>
                  <select
                    value={postedWithin}
                    onChange={(e) => setPostedWithin(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-[#191919] text-sm focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2]"
                    data-testid="posted-within-filter"
                  >
                    <option value="">Anytime</option>
                    <option value="24h">Last 24 hours</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                  </select>
                </div>
              </div>
            )}

            {activeFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="active-filter-chips">
                {activeFilters.map(f => (
                  <button
                    key={f.key}
                    onClick={f.clear}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-[#0a66c2]/10 text-[#0a66c2] border border-[#0a66c2]/30 rounded-full text-xs font-medium hover:bg-[#0a66c2]/20 transition-colors"
                    data-testid={`active-filter-${f.key}`}
                  >
                    {f.label}
                    <X size={12} />
                  </button>
                ))}
                <button
                  onClick={clearAllFilters}
                  className="text-xs font-semibold text-[#666666] hover:text-[#191919] underline ml-1"
                  data-testid="clear-all-filters"
                >
                  Clear all
                </button>
                <button
                  onClick={handleSaveSearch}
                  disabled={savingSearch}
                  className="inline-flex items-center gap-1.5 ml-auto pl-3 pr-3.5 py-1 bg-[#191919] text-white border border-[#191919] rounded-full text-xs font-semibold hover:bg-[#0a66c2] hover:border-[#0a66c2] transition-colors disabled:opacity-50"
                  data-testid="save-search-btn"
                  title={token ? 'Save this filter combination and get a daily email digest' : 'Login to save searches'}
                >
                  <BookmarkPlus size={12} />
                  {savingSearch ? 'Saving…' : 'Save this search'}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* My Saved Searches (logged-in users with at least 1 saved search) */}
        {token && savedSearches.length > 0 && (
          <section
            className="mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
            data-testid="saved-searches-panel"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-sm font-bold text-[#191919] flex items-center gap-2">
                  <Bell className="text-[#0a66c2]" size={16} />
                  My Saved Searches
                </h2>
                <p className="text-xs text-[#666666] mt-0.5">
                  Click a saved search to apply its filters. Daily email digest at 9 AM IST when alerts are on.
                </p>
              </div>
              <span className="text-[10px] font-bold text-[#666666] uppercase tracking-wider whitespace-nowrap">
                {savedSearches.length}/20
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="saved-searches-chips">
              {savedSearches.map(s => {
                const isApplied = appliedSavedId === s.id;
                return (
                  <div
                    key={s.id}
                    className={`group inline-flex items-stretch border rounded-full overflow-hidden text-xs transition-all ${isApplied ? 'border-[#0a66c2] bg-[#0a66c2]/5' : 'border-gray-300 bg-white hover:border-[#0a66c2]/40'}`}
                    data-testid={`saved-search-${s.id}`}
                  >
                    <button
                      onClick={() => applySavedSearch(s)}
                      className={`pl-3 pr-2 py-1.5 font-semibold flex items-center gap-1.5 ${isApplied ? 'text-[#0a66c2]' : 'text-[#191919] hover:text-[#0a66c2]'}`}
                      title="Apply this saved search"
                      data-testid={`saved-search-apply-${s.id}`}
                    >
                      {isApplied && <CheckCircle size={11} />}
                      {s.name}
                    </button>
                    <button
                      onClick={() => toggleSavedAlerts(s)}
                      className={`px-2 py-1.5 border-l ${isApplied ? 'border-[#0a66c2]/30' : 'border-gray-300'} ${s.alerts_enabled ? 'text-[#0a66c2] hover:bg-[#0a66c2]/10' : 'text-[#A3A3A3] hover:text-[#666666] hover:bg-gray-50'} transition-colors`}
                      title={s.alerts_enabled ? 'Email alerts ON · click to pause' : 'Email alerts OFF · click to enable'}
                      data-testid={`saved-search-toggle-${s.id}`}
                    >
                      {s.alerts_enabled ? <Bell size={12} /> : <BellOff size={12} />}
                    </button>
                    <button
                      onClick={() => deleteSavedSearch(s)}
                      className={`px-2 py-1.5 border-l ${isApplied ? 'border-[#0a66c2]/30' : 'border-gray-300'} text-[#A3A3A3] hover:text-[#B91C1C] hover:bg-[#B91C1C]/5 transition-colors`}
                      title="Delete saved search"
                      data-testid={`saved-search-delete-${s.id}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* National Competitions */}
        {competitions.length > 0 && (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-[#191919] mb-4 flex items-center gap-2">
              <Zap className="text-[#915907]" size={22} />
              National Hiring Competitions
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {competitions.map(comp => (
                <div
                  key={comp.id}
                  className="bg-white border border-[#915907]/30 rounded-xl p-5 hover:shadow-md hover:border-[#915907]/50 transition-all"
                  data-testid={`competition-card-${comp.id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="text-[#915907]" size={18} />
                    <span className="text-[#915907] text-xs font-semibold">Featured Competition</span>
                  </div>
                  <h3 className="text-base font-semibold text-[#191919] mb-2">{comp.title}</h3>
                  <p className="text-[#666666] text-sm mb-4 line-clamp-2">{comp.description}</p>
                  <div className="flex items-center gap-3 text-sm text-[#666666] mb-4">
                    <span className="flex items-center gap-1">
                      <Building2 size={14} />
                      {comp.company_name}
                    </span>
                    {comp.application_deadline && (
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        {new Date(comp.application_deadline).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/codearena/jobs/${comp.id}`}
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#915907] text-white rounded-full font-medium text-sm hover:bg-[#6d4305] transition-colors"
                  >
                    View Competition
                    <ChevronRight size={16} />
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Job Listings */}
        <section>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-[#191919]" data-testid="jobs-count-header">
              {processedJobs.length} {processedJobs.length === 1 ? 'Opportunity' : 'Opportunities'}
              {processedJobs.length > 0 && (
                <span className="text-sm font-normal text-[#666666] ml-2">
                  · Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, processedJobs.length)}
                </span>
              )}
            </h2>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#0a66c2]" size={32} />
            </div>
          ) : processedJobs.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="no-jobs-empty-state">
              <Briefcase className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-[#191919] mb-2">No jobs found</h3>
              <p className="text-[#666666] text-sm mb-4">Try adjusting your search or filters</p>
              {activeFilters.length > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0a66c2] text-white rounded-full text-sm font-semibold hover:bg-[#004182] transition-colors"
                  data-testid="empty-state-clear-filters"
                >
                  <X size={14} /> Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedJobs.map((job, idx) => (
                <div
                  key={job.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-[#0a66c2]/30 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => setSelectedJob(job)}
                  data-testid={`job-card-${idx}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      {/* Company Logo */}
                      <div className="w-12 h-12 bg-gradient-to-br from-[#0a66c2] to-[#004182] rounded-lg flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
                        {job.company_name?.charAt(0) || 'C'}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-[#191919] group-hover:text-[#0a66c2] transition-colors mb-1">
                          {job.title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-[#666666] mb-2">
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {job.company_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin size={14} />
                            {job.location}
                          </span>
                          {job.salary_range && (
                            <span className="flex items-center gap-1">
                              <DollarSign size={14} />
                              {job.salary_range}
                            </span>
                          )}
                        </div>
                        
                        {/* Skills */}
                        {job.required_skills?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {job.required_skills.slice(0, 4).map((skill, sidx) => (
                              <span key={sidx} className="px-2 py-0.5 bg-[#f3f2ef] text-[#666666] rounded text-xs border border-gray-200">
                                {skill}
                              </span>
                            ))}
                            {job.required_skills.length > 4 && (
                              <span className="px-2 py-0.5 text-[#666666] text-xs">
                                +{job.required_skills.length - 4} more
                              </span>
                            )}
                          </div>
                        )}
                        
                        {/* Perks */}
                        {job.perks?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {job.perks.slice(0, 3).map((perk, idx) => (
                              <span key={idx} className="flex items-center gap-1 px-2 py-0.5 bg-[#057642]/10 text-[#057642] rounded text-xs">
                                <Gift size={10} />
                                {perk}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex md:flex-col items-center md:items-end gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getJobTypeStyle(job.job_type)}`}>
                        {job.job_type}
                      </span>
                      {job.is_hiring_challenge && (
                        <span className="px-2.5 py-1 bg-[#5f4bb6]/10 text-[#5f4bb6] rounded-full text-xs font-medium border border-[#5f4bb6]/30">
                          Hiring Challenge
                        </span>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap justify-end">
                        <span className="text-[#0a66c2] text-sm font-medium group-hover:underline flex items-center gap-1 cursor-pointer"
                          onClick={(e) => { e.preventDefault(); setSelectedJob(job); }}>
                          View Details <ChevronRight size={14} />
                        </span>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            navigate('/codearena/mock-interview', {
                              state: { job_title: job.title, company_name: job.company_name,
                                job_description: `${job.title} at ${job.company_name}\n\n${job.description || ''}${job.required_skills?.length ? `\n\nSkills: ${job.required_skills.join(', ')}` : ''}` }
                            });
                          }}
                          data-testid={`practice-job-${job.id}`}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-[#059669]/30 text-[#059669] bg-[#059669]/5 rounded-full hover:bg-[#059669]/10 transition-all">
                          <MessageSquare size={11} /> Practice
                        </button>
                        {token && (
                          <button
                            onClick={(e) => { e.preventDefault(); setAnalysisJob(job); setShowAnalysis(true); setAnalysisResult(null); }}
                            data-testid={`resume-match-${job.id}`}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium border border-gray-200 text-[#666666] rounded-full hover:border-[#0a66c2] hover:text-[#0a66c2] transition-all">
                            <Sparkles size={11} /> Resume Match
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && processedJobs.length > PAGE_SIZE && (
            <nav
              className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3"
              aria-label="Job listings pagination"
              data-testid="pagination-controls"
            >
              <p className="text-xs text-[#666666]" data-testid="pagination-summary">
                Page <span className="font-semibold text-[#191919]">{safePage}</span> of{' '}
                <span className="font-semibold text-[#191919]">{totalPages}</span>
                {' '}· {processedJobs.length} total
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-[#191919] hover:bg-[#f3f2ef] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="pagination-prev"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                {(() => {
                  // Compact page-number window: always show first, last, current, and neighbours
                  const pages = [];
                  const window = 1;
                  const add = (n) => { if (!pages.includes(n) && n >= 1 && n <= totalPages) pages.push(n); };
                  add(1); add(totalPages); add(safePage);
                  for (let i = 1; i <= window; i++) { add(safePage - i); add(safePage + i); }
                  pages.sort((a, b) => a - b);
                  const out = [];
                  let prev = 0;
                  pages.forEach(p => {
                    if (prev && p - prev > 1) out.push(<span key={`gap-${p}`} className="px-2 text-[#666666] text-sm">…</span>);
                    out.push(
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`min-w-[34px] px-2 py-1.5 text-sm font-semibold rounded-md transition-colors ${p === safePage ? 'bg-[#0a66c2] text-white' : 'border border-gray-300 text-[#191919] hover:bg-[#f3f2ef]'}`}
                        data-testid={`pagination-page-${p}`}
                        aria-current={p === safePage ? 'page' : undefined}
                      >
                        {p}
                      </button>
                    );
                    prev = p;
                  });
                  return out;
                })()}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md text-[#191919] hover:bg-[#f3f2ef] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="pagination-next"
                  aria-label="Next page"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </nav>
          )}
        </section>
      </div>
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-8 shadow-xl border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#0a66c2] to-[#004182] rounded-lg flex items-center justify-center text-white text-xl font-bold">
                    {selectedJob.company_name?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-[#191919]">{selectedJob.title}</h2>
                    <p className="text-[#666666]">{selectedJob.company_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-[#666666] hover:text-[#191919] p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  data-testid="close-job-modal"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Quick Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="text-center p-3 bg-[#f3f2ef] rounded-lg border border-gray-200">
                  <MapPin className="mx-auto text-[#666666] mb-1" size={18} />
                  <p className="text-[#191919] text-sm font-medium">{selectedJob.location}</p>
                </div>
                <div className="text-center p-3 bg-[#f3f2ef] rounded-lg border border-gray-200">
                  <Briefcase className="mx-auto text-[#666666] mb-1" size={18} />
                  <p className="text-[#191919] text-sm font-medium capitalize">{selectedJob.job_type}</p>
                </div>
                <div className="text-center p-3 bg-[#f3f2ef] rounded-lg border border-gray-200">
                  <Users className="mx-auto text-[#666666] mb-1" size={18} />
                  <p className="text-[#191919] text-sm font-medium capitalize">{selectedJob.experience_level || 'Any'}</p>
                </div>
                {selectedJob.salary_range && (
                  <div className="text-center p-3 bg-[#f3f2ef] rounded-lg border border-gray-200">
                    <DollarSign className="mx-auto text-[#666666] mb-1" size={18} />
                    <p className="text-[#191919] text-sm font-medium">{selectedJob.salary_range}</p>
                  </div>
                )}
              </div>
              
              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-[#191919] mb-2">Description</h3>
                <p className="text-[#666666] text-sm whitespace-pre-wrap leading-relaxed">{selectedJob.description}</p>
              </div>
              
              {/* Required Skills */}
              {selectedJob.required_skills?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#191919] mb-2">Required Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedJob.required_skills.map((skill, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#0a66c2]/10 text-[#0a66c2] rounded-full text-sm border border-[#0a66c2]/30">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Perks */}
              {selectedJob.perks?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#191919] mb-2">Perks & Benefits</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedJob.perks.map((perk, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-[#057642]/10 rounded-lg border border-[#057642]/20">
                        <CheckCircle className="text-[#057642]" size={16} />
                        <span className="text-[#057642] text-sm">{perk}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Deadline */}
              {selectedJob.application_deadline && (
                <div className="flex items-center gap-2 p-3 bg-[#915907]/10 rounded-lg border border-[#915907]/20">
                  <Calendar className="text-[#915907]" size={18} />
                  <span className="text-[#915907] text-sm font-medium">
                    Apply by {new Date(selectedJob.application_deadline).toLocaleDateString('en-IN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 bg-[#f3f2ef] rounded-b-xl">
              <button
                onClick={() => applyForJob(selectedJob.id)}
                disabled={applying}
                className="w-full py-3 bg-[#0a66c2] text-white rounded-full font-semibold hover:bg-[#004182] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
                data-testid="apply-job-btn"
              >
                {applying ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Applying...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Apply Now
                  </>
                )}
              </button>
              {!token && (
                <p className="text-center text-[#666666] text-sm mt-2">
                  Please <Link to="/codearena/auth" className="text-[#0a66c2] hover:underline font-medium">login</Link> to apply
                </p>
              )}
              {/* Practice + Resume Match */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    setSelectedJob(null);
                    navigate('/codearena/mock-interview', {
                      state: { job_title: selectedJob.title, company_name: selectedJob.company_name,
                        job_description: `${selectedJob.title} at ${selectedJob.company_name}\n\n${selectedJob.description || ''}${selectedJob.required_skills?.length ? `\n\nSkills: ${selectedJob.required_skills.join(', ')}` : ''}` }
                    });
                  }}
                  data-testid="modal-practice-btn"
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium border border-[#059669]/30 text-[#059669] bg-[#059669]/5 rounded-full hover:bg-[#059669]/10 transition-all">
                  <MessageSquare size={14} /> Practice Interview
                </button>
                {token && (
                  <button
                    onClick={() => { setAnalysisJob(selectedJob); setShowAnalysis(true); setAnalysisResult(null); setSelectedJob(null); }}
                    data-testid="modal-resume-match-btn"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium border border-gray-200 text-[#666666] rounded-full hover:border-[#0a66c2] hover:text-[#0a66c2] transition-all">
                    <Sparkles size={14} /> Resume Match
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resume Analysis Modal */}
      {showAnalysis && analysisJob && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-gray-200 shadow-xl"
            data-testid="resume-analysis-modal">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div>
                <h3 className="text-base font-semibold text-[#191919]">Resume Analysis</h3>
                <p className="text-xs text-[#666666] mt-0.5">
                  Match your resume against <strong>{analysisJob.title}</strong> at {analysisJob.company_name}
                </p>
              </div>
              <button onClick={() => { setShowAnalysis(false); setAnalysisResult(null); }}
                className="p-1 text-[#666666] hover:text-[#191919] transition-colors" data-testid="close-analysis-modal">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {!analysisResult ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-[#666666] mb-2">Upload Resume (PDF)</label>
                    <label className="flex items-center justify-center gap-2 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-all"
                      style={{ borderColor: resumeText ? '#059669' : '#e0e0e0', background: resumeText ? '#05966908' : '#fff' }}>
                      <input type="file" accept=".pdf" className="hidden" onChange={handleResumeUpload} data-testid="jb-resume-upload" />
                      {uploadingResume ? (
                        <><Loader2 size={14} className="animate-spin text-[#666666]" /> <span className="text-xs text-[#666666]">Processing...</span></>
                      ) : resumeText ? (
                        <><FileText size={14} className="text-[#059669]" /> <span className="text-xs font-medium text-[#059669]">Resume loaded</span></>
                      ) : (
                        <><Upload size={14} className="text-[#666666]" /> <span className="text-xs text-[#666666]">Upload PDF Resume</span></>
                      )}
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#666666] mb-2">Or paste resume text</label>
                    <textarea value={resumeText} onChange={e => setResumeText(e.target.value)} rows={4}
                      data-testid="jb-resume-text"
                      placeholder="Paste your resume content here..."
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0a66c2]/20 focus:border-[#0a66c2] resize-none" />
                  </div>
                  <button onClick={runAnalysis} disabled={!resumeText.trim() || analyzing}
                    data-testid="jb-analyze-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-white rounded-full transition-all disabled:opacity-50"
                    style={{ background: '#0a66c2' }}>
                    {analyzing ? <><Loader2 size={14} className="animate-spin" /> Analyzing...</> : <><Sparkles size={14} /> Analyze Match</>}
                  </button>
                </>
              ) : (
                <div className="space-y-5" data-testid="jb-analysis-results">
                  <div className="text-center py-4">
                    <div className="text-5xl font-black" data-testid="jb-match-score"
                      style={{ color: analysisResult.match_score >= 70 ? '#059669' : analysisResult.match_score >= 40 ? '#D97706' : '#DC2626' }}>
                      {analysisResult.match_score}%
                    </div>
                    <p className="text-xs font-bold uppercase mt-1"
                      style={{ color: analysisResult.match_score >= 70 ? '#059669' : analysisResult.match_score >= 40 ? '#D97706' : '#DC2626' }}>
                      {analysisResult.match_score >= 70 ? 'STRONG MATCH' : analysisResult.match_score >= 40 ? 'PARTIAL MATCH' : 'LOW MATCH'}
                    </p>
                  </div>
                  {analysisResult.summary && <p className="text-sm text-[#666666] leading-relaxed p-3 bg-[#f3f2ef] rounded-lg">{analysisResult.summary}</p>}
                  {analysisResult.missing_skills?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#666666] uppercase mb-2">Missing Skills</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {analysisResult.missing_skills.map((s, i) => (
                          <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full border border-red-200 text-red-600 bg-red-50">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.matching_skills?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#666666] uppercase mb-2">Matching Skills</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {analysisResult.matching_skills.map((s, i) => (
                          <span key={i} className="text-xs font-medium px-2.5 py-1 rounded-full border border-green-200 text-green-600 bg-green-50">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {analysisResult.suggestions?.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-[#666666] uppercase mb-2">Suggestions</h4>
                      <div className="space-y-2">
                        {analysisResult.suggestions.map((s, i) => (
                          <div key={i} className="flex gap-2 p-2.5 bg-[#f3f2ef] rounded-lg">
                            <span className="text-xs font-bold text-[#0a66c2] shrink-0">{i + 1}.</span>
                            <p className="text-xs text-[#666666] leading-relaxed">{s}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button onClick={() => setAnalysisResult(null)} className="flex-1 py-2.5 text-xs font-medium border border-gray-200 rounded-full">Try Again</button>
                    <button onClick={() => {
                        navigate('/codearena/mock-interview', {
                          state: { job_title: analysisJob.title, company_name: analysisJob.company_name,
                            job_description: `${analysisJob.title} at ${analysisJob.company_name}\n\n${analysisJob.description || ''}` }
                        });
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium text-white rounded-full"
                      style={{ background: '#059669' }}>
                      <MessageSquare size={12} /> Practice Interview
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobBoardPage;
